import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { OlaMaps } from "olamaps-web-sdk";
import { olaMapsApiKey } from "../../config";

const clampZoom = (zoom: number) => Math.max(0, Math.min(15, Math.round(zoom)));

const LocationMapView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any | null>(null);
  const markerRef = useRef<any | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);

  const location = useMemo(() => {
    const lat = parseFloat(searchParams.get("lat") || "");
    const lng = parseFloat(searchParams.get("lng") || "");
    const zoomRaw = parseFloat(searchParams.get("zoom") || "");
    const label = searchParams.get("label") || "";

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }

    return {
      lat,
      lng,
      zoom: Number.isNaN(zoomRaw) ? 14 : clampZoom(zoomRaw),
      label: label.trim(),
    };
  }, [searchParams]);

  useEffect(() => {
    if (!location || !mapContainerRef.current || mapRef.current) {
      return;
    }

    const initMap = async () => {
      try {
        const olaMaps = new OlaMaps({ apiKey: olaMapsApiKey });
        const myMap = await olaMaps.init({
          style:
            "https://api.olamaps.io/tiles/vector/v1/styles/default-dark-standard/style.json",
          container: mapContainerRef.current,
          center: [location.lng, location.lat],
          zoom: location.zoom,
        });

        myMap.on("load", () => {
          if (myMap.getLayer("3d_model_data")) {
            myMap.removeLayer("3d_model_data");
          }
        });

        mapRef.current = myMap;
        setIsMapLoaded(true);
        setMapError(false);
      } catch {
        setMapError(true);
      }
    };

    void initMap();
  }, [location]);

  useEffect(() => {
    if (!mapRef.current || !isMapLoaded || !location) {
      return;
    }

    if (markerRef.current) {
      try {
        markerRef.current.remove();
      } catch {
        // ignore
      }
      markerRef.current = null;
    }

    const popupText =
      location.label ||
      `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
    const popup = new (OlaMaps as any).Popup({
      offset: [0, -10],
      anchor: "bottom",
    }).setHTML(
      `<div style="font-family: system-ui, -apple-system, sans-serif; font-size: 12px; color: #000; max-width: 240px;">${popupText}</div>`
    );

    const markerElement = document.createElement("div");
    markerElement.style.height = "25px";
    markerElement.style.width = "25px";
    markerElement.style.borderRadius = "50%";
    markerElement.style.border = "2px solid #fcd307";
    markerElement.style.backgroundColor = "#1e293b";
    markerElement.style.boxShadow = "0 0 10px rgba(252, 211, 7, 0.6)";

    markerRef.current = new (OlaMaps as any).Marker({
      element: markerElement,
      draggable: false,
    })
      .setLngLat([location.lng, location.lat])
      .setPopup(popup)
      .addTo(mapRef.current);

    markerRef.current.togglePopup();
  }, [isMapLoaded, location]);

  if (!location) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] text-gray-400">
        Invalid location coordinates
      </div>
    );
  }

  if (mapError) {
    return (
      <div className="flex items-center justify-center h-full min-h-[300px] text-red-400">
        Failed to load map
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full gap-3 min-h-0">
      <div className="px-1 shrink-0">
        <h1 className="text-lg font-semibold text-yellow-500 text-left">
          Teaching Location
        </h1>
        {location.label ? (
          <p className="text-sm text-gray-400 text-left mt-1">{location.label}</p>
        ) : null}
      </div>
      <div
        ref={mapContainerRef}
        className="flex-1 min-h-[400px] w-full rounded-lg overflow-hidden border border-gray-700"
      />
    </div>
  );
};

export default LocationMapView;
